/**
 * Single source of truth for responsive breakpoints.
 *
 * Add / change a breakpoint here and the whole system follows: the editor
 * switcher, the canvas viewport sizes, the production width→breakpoint mapping,
 * and the override storage keys.
 */

export type BreakpointId =
  | 'default'
  | 'ultrawide'
  | 'desktop'
  | 'tablet'
  | 'mobile'

/** A breakpoint that can hold overrides (everything except `default`). */
export type OverrideBreakpointId = Exclude<BreakpointId, 'default'>

export interface Breakpoint {
  id: BreakpointId
  label: string
  /** Canvas width (px) used by Puck's `viewports` for the editor preview. */
  canvasWidth?: number
  /** Production matchMedia lower bound (px, inclusive). Open-ended if omitted. */
  minWidth?: number
  /** Production matchMedia upper bound (px, inclusive). Open-ended if omitted. */
  maxWidth?: number
}

export const DEFAULT_BREAKPOINT: BreakpointId = 'default'

/**
 * Cascade order matters: `breakpointForWidth` returns the first match, so the widest
 * range comes first.
 *
 * `default` is not a width — it is the base every breakpoint merges over, and it is what
 * the server renders. A reader sees it until the bundle hydrates, so whichever audience it
 * describes is the one that never sees a change of layout on load. That makes it the place
 * for the ordinary desktop design, with `ultrawide` carrying only what a very wide screen
 * needs; authoring it the other way round shows every desktop visitor the ultrawide layout
 * first.
 *
 * `ultrawide` starts above 1680 because that is where a 1280 desktop design stops filling
 * the screen convincingly. It is a judgement rather than a standard, and moving it is a
 * one-line change here.
 */
export const BREAKPOINTS: readonly Breakpoint[] = [
  { id: 'default', label: 'Default' },
  { id: 'ultrawide', label: 'Ultrawide', canvasWidth: 1920, minWidth: 1681 },
  {
    id: 'desktop',
    label: 'Desktop',
    canvasWidth: 1280,
    minWidth: 1025,
    maxWidth: 1680,
  },
  { id: 'tablet', label: 'Tablet', canvasWidth: 768, minWidth: 641, maxWidth: 1024 },
  { id: 'mobile', label: 'Mobile', canvasWidth: 390, maxWidth: 640 },
]

/** Override-capable breakpoints (everything except `default`), in cascade order. */
export const OVERRIDE_BREAKPOINTS: readonly Breakpoint[] = BREAKPOINTS.filter(
  (b): b is Breakpoint & { id: OverrideBreakpointId } => b.id !== 'default',
)

export const getBreakpoint = (id: BreakpointId): Breakpoint | undefined =>
  BREAKPOINTS.find(b => b.id === id)

export const isOverrideBreakpoint = (
  id: BreakpointId,
): id is OverrideBreakpointId => id !== 'default'

/** Map a Puck canvas width back to a breakpoint (matches `canvasWidth`). */
export function breakpointForCanvasWidth(width: number): BreakpointId {
  return (
    BREAKPOINTS.find(b => b.canvasWidth === width)?.id ?? DEFAULT_BREAKPOINT
  )
}

/**
 * Pick the breakpoint whose media range contains a viewport width (production).
 * Ranges are disjoint; falls back to `default` when nothing matches.
 */
export function breakpointForWidth(width: number): BreakpointId {
  const match = OVERRIDE_BREAKPOINTS.find(
    b =>
      (b.minWidth === undefined || width >= b.minWidth) &&
      (b.maxWidth === undefined || width <= b.maxWidth),
  )
  return match?.id ?? DEFAULT_BREAKPOINT
}
